import * as React from 'react';
import { action } from 'mobx';
import { observer } from 'mobx-react';
import ReactTable, { CellInfo, Column } from 'react-table';
import { Label, Icon } from 'office-ui-fabric-react';

import { PersonLicenseViewModel, PlanningLicenseInactivationModel } from '@app/Models';
import { Permissions } from '@app/AppConstants';

import { NewLicenseInactivationDialog } from '@app/Pages/PersonInfo/Components/Dialogs/NewLicenseInactivationDialog';
import DateTimeService from '@app/Services/DateTimeService';
import { securityService } from '@app/Services/SecurityService';
import { InlineDateCell } from '@app/Components/CellFormatters/InlineDateCell';
import { AzureAuth } from '@app/Components/AzureAuth';
import InlineDateValidationService from '@app/Services/InlineDateValidationService';
import { InlineTextboxCellWithTooltip } from '@app/Components/CellFormatters/InlineTextboxCellWithTooltip';
import { ChangeLicenseInactivationCallback, LicenseInactivationStore } from '@app/Pages/PersonInfo/Stores/LicenseInactivationStore';
import { modalService, ModalButtonType, ModalWindow, ModalDialogOptions, IModalDialogContent } from '@app/Components/Modal';

type ExploreLicenseDialogProps = {
    personLicense: PersonLicenseViewModel;
    personId: string;
    onChangeLicenseInactivationHandler: ChangeLicenseInactivationCallback;
};

@observer
export class ExploreLicenseDialog extends React.Component<ExploreLicenseDialogProps, {}> implements IModalDialogContent<void> {
    private _store: LicenseInactivationStore;
    
    constructor(props: ExploreLicenseDialogProps) {
        super(props);
        this._store = new LicenseInactivationStore({licenseId: props.personLicense.id, personId: props.personId, onChangeLicenseInactivationHandler: this.props.onChangeLicenseInactivationHandler});
    }

    public getModalOptions(window: ModalWindow<void>): ModalDialogOptions<void> {
        return {
            title: 'License Inactivations',
            width: '600px',
            buttons: [{
                type: ModalButtonType.Cancel,
                onClick: () => {
                    window.close();
                }
            }],
            loader: this._store.loader
        };
    }

    render() {
        let headerDateRange = this.props.personLicense.dateFrom ? '( ' + DateTimeService.toUiDate(this.props.personLicense.dateFrom) + ' / ' : '( - / ';
        headerDateRange += this.props.personLicense.dateTo ? DateTimeService.toUiDate(this.props.personLicense.dateTo) + ' )' : '- )';

        if (!this.props.personLicense.dateFrom && !this.props.personLicense.dateTo) {
            headerDateRange = '';
        }
        const { hasPermission, permissionCss } = securityService;
        return (
            <>
                <div className="table-label">
                    <Label><span className="license-headline">For '{this.props.personLicense.license}' {headerDateRange}</span></Label>
                    {hasPermission(Permissions.ViewAddObjectPersonLicenseInactivation, !AzureAuth.isReadOnly) &&
                    <button className={'table-label-button ' + permissionCss(Permissions.ViewObjectPersonLicenseInactivation, Permissions.EditObjectPersonLicenseInactivation)} onClick={this._addLicenseInactivation}>
                        <Icon iconName="Add" className="ms-IconTable" />
                    </button>}
                </div>
                <ReactTable<PlanningLicenseInactivationModel>
                    data={this._store.personLicenseInactivations}
                    columns={this._licenceInactivationColumns}
                    defaultPageSize={10}
                    showPageSizeOptions={false}
                    resizable={true}
                    showPagination={false}
                    className={'ReactTable-fixed-height-modal'}
                />
            </>
        );
    }

    @action.bound
    private async  _addLicenseInactivation() {
        const { personLicense, onChangeLicenseInactivationHandler } = this.props;
        const { button } = await modalService.show(NewLicenseInactivationDialog, {
            licenseDateFrom: personLicense.dateFrom,
            licenseDateTo: personLicense.dateTo,
            licenseId: personLicense.id
        });

        if (button === ModalButtonType.Save){
            await this._store.loadLicenseInactivations(this.props.personLicense.id);
        }

        onChangeLicenseInactivationHandler(this.props.personId);
    }
    
    @action.bound
    private _renderInactivationDateFrom(cellInfo: CellInfo){
        return <InlineDateCell<PlanningLicenseInactivationModel>
            onValidate={InlineDateValidationService.validateInactivationDate} 
            value={cellInfo.value} 
            onSelect={this._updateLicenseInactivation} 
            row={cellInfo.row._original}
            fieldKey={cellInfo.column.id as keyof PlanningLicenseInactivationModel} 
            minDate={this.props.personLicense.dateFrom}
            maxDate={this.props.personLicense.dateTo}
            isReadOnly={!securityService.hasPermission(Permissions.EditPersonLicenseInactivationDateFrom, !AzureAuth.isReadOnly)}/>;
    }

    @action.bound
    private _renderInactivationDateTo(cellInfo: CellInfo){
        return <InlineDateCell<PlanningLicenseInactivationModel>
            onValidate={InlineDateValidationService.validateInactivationDate}
            value={cellInfo.value} 
            onSelect={this._updateLicenseInactivation}
            row={cellInfo.row._original} 
            fieldKey={cellInfo.column.id as keyof PlanningLicenseInactivationModel} 
            minDate={this.props.personLicense.dateFrom} 
            maxDate={this.props.personLicense.dateTo} 
            canBeEmpty 
            isReadOnly={!securityService.hasPermission(Permissions.EditPersonLicenseInactivationDateTo, !AzureAuth.isReadOnly)}
        />;
    }

    @action.bound
    private _renderInactivationComment(cellInfo: CellInfo){
        return <InlineTextboxCellWithTooltip<PlanningLicenseInactivationModel>
            cellInfo={cellInfo} 
            value={cellInfo.value} 
            onChange={this._updateLicenseInactivation} 
            row={cellInfo.row._original} 
            fieldKey="comment"
            disabled={!securityService.hasPermission(Permissions.EditPersonLicenseInactivationComment, !AzureAuth.isReadOnly)} 
        />;
    }

    @action.bound
    private _renderDeleteLicenseInactivationColumn(cellInfo: CellInfo) {
        return <i className="ms-Icon ms-Icon-action ms-Icon--Delete" aria-hidden="true" onClick={async () => this.onDeleteLicenseInactivation(cellInfo)} />;
    }

    private async onDeleteLicenseInactivation(cellInfo: CellInfo) {
        await modalService.showConfirmation('Are you sure you want to delete this license inactivation?', 'Warning', {
            onConfirm: async () => {
                await this._store.deleteLicenseInactivation(cellInfo.value, this.props.personLicense.id);
            }
        });
    }

    @action.bound
    private _updateLicenseInactivation(params: PlanningLicenseInactivationModel){
        this._store.updateLicenseInactivation(params);
    }

    private _licenceInactivationColumns: Column<PlanningLicenseInactivationModel>[] = [
        {
            Header: 'From',
            accessor: 'dateFrom',
            Cell: this._renderInactivationDateFrom,
            maxWidth: 150,
            show: securityService.hasPermission(Permissions.ViewPersonLicenseInactivationDateFrom),
            className: securityService.permissionCss(Permissions.ViewPersonLicenseInactivationDateFrom, Permissions.EditPersonLicenseInactivationDateFrom)
        },
        {
            Header: 'To',
            accessor: 'dateTo',
            Cell: this._renderInactivationDateTo,
            maxWidth: 150,
            show: securityService.hasPermission(Permissions.ViewPersonLicenseInactivationDateFrom),
            className: securityService.permissionCss(Permissions.ViewPersonLicenseInactivationDateTo, Permissions.EditPersonLicenseInactivationDateTo)
        },
        {
            Header: 'Comment',
            accessor: 'comment',
            Cell: this._renderInactivationComment,
            show: securityService.hasPermission(Permissions.ViewPersonLicenseInactivationDateFrom),
            className: securityService.permissionCss(Permissions.ViewPersonLicenseInactivationComment, Permissions.EditPersonLicenseInactivationComment)
        },
        {
            Header: '',
            accessor: 'id',
            sortable: false,
            Cell: this._renderDeleteLicenseInactivationColumn,
            maxWidth: 50,
            className: securityService.permissionCss(Permissions.ViewObjectPersonLicenseInactivation, Permissions.EditObjectPersonLicenseInactivation),
            show: securityService.hasPermission(Permissions.EditObjectPersonLicenseInactivation, !AzureAuth.isReadOnly) 
        }
    ];
}